// =============================================================================
// sales — admin sales feed (Bokun replica)
// =============================================================================
import { supabase } from './supabase';

// 'online' = our own site, 'partner' = a partner's booking form (20261105170000).
export type SalesSource = 'online' | 'offline' | 'partner';

export interface SalesRow {
  id: string;
  booking_number: string;
  lifecycle_status: string;
  payment_status: string;
  party_size: number;
  total_cents: number | null;
  locale: string;
  created_at: string;
  // Name typed at booking time; prefer over first/last. Null on older
  // rows and on payloads from an older fn_admin_list_sales.
  guest_name?: string | null;
  first_name: string | null;
  last_name: string | null;
  customer_email: string | null;   // null for anonymous counter walk-ins
  customer_phone: string | null;
  experience_name: string | null;
  first_slot_start: string | null;
  first_room_name: string | null;
  first_room_id: string | null;
  slots_count: number;
  source: SalesSource;
  // Set when source is 'partner'. Absent on an older fn_admin_list_sales.
  partner_name?: string | null;
  operator_email: string | null;
  discount: { code: string; amount_off_cents: number } | null;
  // 'booking' (default) or 'voucher' — gift-voucher sales are surfaced as
  // synthetic rows (booking_number = voucher code, no slot / room / location).
  // Optional so an older fn_admin_list_sales (no kind) is treated as a booking.
  // 'gift_card' = a physical gift card load at the counter (booking_number =
  // the card code XXXX-XXXX-XXXX, venue = where it was loaded).
  kind?: 'booking' | 'voucher' | 'gift_card';
}

export interface SalesFeedResult {
  rows: SalesRow[];
  total: number;
  limit: number;
  offset: number;
}

export interface SalesFeedFilter {
  search?: string | null;
  lifecycleStatuses?: string[] | null;
  source?: SalesSource | null;
  bookedFrom?: string | null;
  bookedTo?: string | null;
  travelledFrom?: string | null;
  travelledTo?: string | null;
  locationId?: string | null;
  limit?: number;
  offset?: number;
}

export async function listSales(
  filter: SalesFeedFilter = {},
): Promise<SalesFeedResult> {
  const { data, error } = await supabase
    .schema('booking')
    .rpc('fn_admin_list_sales', {
      p_search:             filter.search && filter.search.trim().length > 0
                              ? filter.search.trim()
                              : null,
      p_lifecycle_statuses: filter.lifecycleStatuses ?? null,
      p_source:             filter.source ?? null,
      p_booked_from:        filter.bookedFrom ?? null,
      p_booked_to:          filter.bookedTo ?? null,
      p_travelled_from:     filter.travelledFrom ?? null,
      p_travelled_to:       filter.travelledTo ?? null,
      p_limit:              filter.limit ?? 50,
      p_offset:             filter.offset ?? 0,
      p_location_id:        filter.locationId ?? null,
    });
  if (error) throw error;
  return (data as SalesFeedResult) ?? { rows: [], total: 0, limit: 50, offset: 0 };
}
